import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const DieselContext = createContext(null);

export function useDiesel() {
    const context = useContext(DieselContext);
    if (!context) {
        throw new Error('useDiesel debe usarse dentro de DieselProvider');
    }
    return context;
}

export function DieselProvider({ children }) {
    const { user } = useAuth();
    const [config, setConfig] = useState(null);
    const [permisos, setPermisos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            loadDieselData();
        }
    }, [user]);

    const loadDieselData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Cargar configuración y permisos en paralelo
            const [configRes, permisosRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diesel/config`, { headers }),
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diesel/mis-permisos`, { headers })
            ]);

            if (configRes.ok) {
                const configData = await configRes.json();
                setConfig(configData);
                
                // Aplicar tema personalizado
                applyTheme(configData);
            }

            if (permisosRes.ok) {
                const permisosData = await permisosRes.json();
                setPermisos(permisosData.permisos || []);
            }
        } catch (err) {
            console.error('Error loading diesel data:', err);
        } finally {
            setLoading(false);
        }
    };

    const applyTheme = (cfg) => {
        if (!cfg) return;
        
        const root = document.documentElement;
        
        // Convertir hex a HSL para las variables CSS
        if (cfg.color_primario) {
            root.style.setProperty('--diesel-primary', cfg.color_primario);
        }
        if (cfg.color_secundario) {
            root.style.setProperty('--diesel-secondary', cfg.color_secundario);
        }
        if (cfg.color_acento) {
            root.style.setProperty('--diesel-accent', cfg.color_acento);
        }
    };

    const hasPermission = (permiso) => {
        return permisos.includes(permiso);
    };

    const hasAnyPermission = (...permisosRequeridos) => {
        return permisosRequeridos.some(p => permisos.includes(p));
    };

    const hasAllPermissions = (...permisosRequeridos) => {
        return permisosRequeridos.every(p => permisos.includes(p));
    };

    const refreshConfig = async () => {
        await loadDieselData();
    };

    const value = {
        config,
        permisos,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshConfig,
        nombreSistema: config?.nombre_sistema || 'Diesel',
        logoUrl: config?.logo_url,
    };

    return (
        <DieselContext.Provider value={value}>
            {children}
        </DieselContext.Provider>
    );
}
